'use client';
import React, { useState } from 'react';
import { Shield, Clock, Upload, Star, BookOpen, FileText, Award, Eye, Send, Layers, HelpCircle, Folder } from 'lucide-react';

import Modal from '@/components/ui/Modal';
import Icon from '@/components/ui/AppIcon';


type SubmissionStatus = 'draft' | 'submitted' | 'peer_review' | 'admin_review' | 'published' | 'revision_needed';

interface Educator {
  id: string;
  name: string;
  title: string;
  institution: string;
  credentials: string[];
  verified: boolean;
  coursesPublished: number;
  rating: number;
  avatar: string;
}

interface Submission {
  id: string;
  title: string;
  type: 'course' | 'module' | 'lesson' | 'quiz' | 'project';
  author: string;
  submittedAt: string;
  status: SubmissionStatus;
  peerReviewer?: string;
  adminReviewer?: string;
  note?: string;
  wing: string;
}

const educators: Educator[] = [
  { id: 'edu-001', name: 'Dr. Nadia Volkov', title: 'AI Research Scientist', institution: 'MIT', credentials: ['PhD Computer Science', 'AI Safety Researcher', 'Published Author'], verified: true, coursesPublished: 4, rating: 4.9, avatar: 'NV' },
  { id: 'edu-002', name: 'Prof. Marcus Chen', title: 'Mathematics Professor', institution: 'Stanford University', credentials: ['PhD Mathematics', 'Fields Medal Nominee', 'Curriculum Designer'], verified: true, coursesPublished: 7, rating: 4.8, avatar: 'MC' },
  { id: 'edu-003', name: 'Col. James Rivera', title: 'Aerospace Engineer', institution: 'NASA JPL', credentials: ['MS Aerospace Engineering', 'Mission Specialist', 'STEM Educator'], verified: true, coursesPublished: 2, rating: 4.7, avatar: 'JR' },
  { id: 'edu-004', name: 'Dr. Aisha Patel', title: 'Computer Science Educator', institution: 'Carnegie Mellon', credentials: ['PhD CS Education', 'Google Faculty Fellow'], verified: false, coursesPublished: 0, rating: 0, avatar: 'AP' },
];

const submissions: Submission[] = [
  { id: 'sub-001', title: 'Quantum Computing Fundamentals', type: 'course', author: 'Dr. Nadia Volkov', submittedAt: '2026-07-09', status: 'peer_review', peerReviewer: 'Prof. Marcus Chen', wing: 'professional' },
  { id: 'sub-002', title: 'Proof-Based Reasoning Module', type: 'module', author: 'Prof. Marcus Chen', submittedAt: '2026-07-07', status: 'admin_review', peerReviewer: 'Dr. Nadia Volkov', adminReviewer: 'Admin Steward', wing: 'advanced' },
  { id: 'sub-003', title: 'Orbital Mechanics Lesson Set', type: 'lesson', author: 'Col. James Rivera', submittedAt: '2026-07-05', status: 'published', wing: 'professional' },
  { id: 'sub-004', title: 'Python Data Structures Quiz Bank', type: 'quiz', author: 'Dr. Aisha Patel', submittedAt: '2026-07-08', status: 'revision_needed', note: 'Please add difficulty ratings to each question.', wing: 'intermediate' },
  { id: 'sub-005', title: 'Capstone: Build a Neural Network', type: 'project', author: 'Dr. Nadia Volkov', submittedAt: '2026-07-10', status: 'draft', wing: 'professional' },
];

const statusConfig: Record<SubmissionStatus, { label: string; color: string }> = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-600 border-slate-200' },
  submitted: { label: 'Submitted', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  peer_review: { label: 'Peer Review', color: 'bg-purple-50 text-purple-700 border-purple-200' },
  admin_review: { label: 'Admin Review', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  published: { label: 'Published', color: 'bg-green-50 text-green-700 border-green-200' },
  revision_needed: { label: 'Revision Needed', color: 'bg-red-50 text-red-700 border-red-200' },
};

const typeIcon: Record<string, React.ElementType> = {
  course: BookOpen, module: Layers, lesson: FileText, quiz: HelpCircle, project: Folder,
};

const typeColor: Record<string, string> = {
  course: 'text-blue-600 bg-blue-50', module: 'text-purple-600 bg-purple-50',
  lesson: 'text-emerald-600 bg-emerald-50', quiz: 'text-amber-600 bg-amber-50',
  project: 'text-rose-600 bg-rose-50',
};

const wingColor: Record<string, string> = {
  foundations: 'text-emerald-600', intermediate: 'text-blue-600',
  advanced: 'text-purple-600', professional: 'text-amber-600',
};

export default function ProfessorPortalContent() {
  const [activeTab, setActiveTab] = useState<'educators' | 'submissions' | 'submit'>('educators');
  const [selectedEducator, setSelectedEducator] = useState<Educator | null>(null);
  const [educatorModalOpen, setEducatorModalOpen] = useState(false);
  const [submitStep, setSubmitStep] = useState(1);
  const [contentType, setContentType] = useState('course');

  const stats = [
    { label: 'Verified Educators', value: educators.filter(e => e.verified).length, icon: Shield, color: 'text-green-600 bg-green-50' },
    { label: 'Pending Verification', value: educators.filter(e => !e.verified).length, icon: Clock, color: 'text-amber-600 bg-amber-50' },
    { label: 'Active Submissions', value: submissions.filter(s => s.status !== 'published').length, icon: Send, color: 'text-blue-600 bg-blue-50' },
    { label: 'Published Content', value: submissions.filter(s => s.status === 'published').length, icon: Award, color: 'text-purple-600 bg-purple-50' },
  ];

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-foreground">Professor Upload Portal</h1>
          <p className="text-sm text-muted-foreground mt-1">Verified educator workspace for curriculum submission, peer review, and publishing</p>
        </div>
        <button
          onClick={() => setActiveTab('submit')}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-semibold btn-scale hover:bg-primary/90 transition-colors"
        >
          <Upload size={16} /> Submit Curriculum
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="bg-card border border-border rounded-2xl p-4 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${s.color}`}>
                <Icon size={18} />
              </div>
              <div>
                <p className="text-xl font-extrabold text-foreground font-tabular">{s.value}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-border">
        {(['educators', 'submissions', 'submit'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2.5 text-sm font-semibold capitalize transition-all border-b-2 -mb-px ${
              activeTab === tab ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab === 'educators' ? 'Verified Educators' : tab === 'submissions' ? 'Submission Pipeline' : 'Submit Content'}
          </button>
        ))}
      </div>

      {/* Educators Tab */}
      {activeTab === 'educators' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {educators.map((edu) => (
            <div key={edu.id} className="bg-card border border-border rounded-2xl p-5 card-hover">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
                  <span className="text-sm font-bold text-primary">{edu.avatar}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className="text-sm font-bold text-foreground">{edu.name}</h3>
                    {edu.verified ? (
                      <span className="flex items-center gap-0.5 text-xs text-green-700 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded-full font-semibold">
                        <Shield size={9} /> Verified
                      </span>
                    ) : (
                      <span className="flex items-center gap-0.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full font-semibold">
                        <Clock size={9} /> Pending
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">{edu.title} · {edu.institution}</p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {edu.credentials.map((c) => (
                      <span key={c} className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">{c}</span>
                    ))}
                  </div>
                  {edu.verified && (
                    <div className="flex items-center gap-3 mt-3 pt-3 border-t border-border">
                      <span className="text-xs text-muted-foreground"><span className="font-semibold text-foreground">{edu.coursesPublished}</span> published</span>
                      <span className="flex items-center gap-0.5 text-xs text-amber-600"><Star size={11} fill="currentColor" /> {edu.rating}</span>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => { setSelectedEducator(edu); setEducatorModalOpen(true); }}
                  className="p-2 rounded-lg hover:bg-muted transition-colors"
                >
                  <Eye size={14} className="text-muted-foreground" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Submissions Tab */}
      {activeTab === 'submissions' && (
        <div className="space-y-3">
          {submissions.map((sub) => {
            const TypeIcon = typeIcon[sub.type];
            return (
              <div key={sub.id} className="bg-card border border-border rounded-2xl p-5 card-hover">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex items-start gap-3 flex-1">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${typeColor[sub.type]}`}>
                      <TypeIcon size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-xs font-semibold capitalize text-muted-foreground">{sub.type}</span>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${statusConfig[sub.status].color}`}>
                          {statusConfig[sub.status].label}
                        </span>
                        <span className={`text-xs font-semibold capitalize ${wingColor[sub.wing]}`}>{sub.wing}</span>
                      </div>
                      <p className="text-sm font-bold text-foreground">{sub.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">by {sub.author} · {sub.submittedAt}</p>
                      {sub.peerReviewer && (
                        <p className="text-xs text-muted-foreground mt-0.5">Peer reviewer: <span className="font-semibold">{sub.peerReviewer}</span></p>
                      )}
                      {sub.note && (
                        <div className="mt-2 text-xs bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg">
                          <span className="font-semibold">Revision note:</span> {sub.note}
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Pipeline steps */}
                  <div className="flex items-center gap-1 shrink-0">
                    {(['submitted', 'peer_review', 'admin_review', 'published'] as SubmissionStatus[]).map((stage, i) => {
                      const stages: SubmissionStatus[] = ['submitted', 'peer_review', 'admin_review', 'published'];
                      const currentIdx = stages.indexOf(sub.status);
                      const stageIdx = stages.indexOf(stage);
                      const done = currentIdx > stageIdx || sub.status === stage;
                      return (
                        <React.Fragment key={stage}>
                          <div className={`w-2 h-2 rounded-full ${done ? 'bg-primary' : 'bg-muted'}`} />
                          {i < 3 && <div className={`w-4 h-0.5 ${done && currentIdx > stageIdx ? 'bg-primary' : 'bg-muted'}`} />}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Submit Content Tab */}
      {activeTab === 'submit' && (
        <div className="max-w-2xl space-y-5">
          <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
            <h2 className="text-base font-bold text-foreground">Submit New Curriculum Content</h2>
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 block">Content Type</label>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {(['course', 'module', 'lesson', 'quiz', 'project'] as const).map((t) => {
                  const Icon = typeIcon[t];
                  return (
                    <button
                      key={t}
                      onClick={() => setContentType(t)}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border text-xs font-semibold capitalize transition-all ${
                        contentType === t ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-muted/30 text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                    >
                      <Icon size={16} /> {t}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 block">Title</label>
                <input className="w-full bg-input border border-border rounded-xl px-3 py-2.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/30" placeholder="Content title..." />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 block">Campus Wing</label>
                <select className="w-full bg-input border border-border rounded-xl px-3 py-2.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/30">
                  <option>Foundations</option><option>Intermediate</option><option>Advanced</option><option>Professional</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 block">Difficulty</label>
                <select className="w-full bg-input border border-border rounded-xl px-3 py-2.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/30">
                  <option>Beginner</option><option>Intermediate</option><option>Advanced</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 block">Description</label>
                <textarea rows={3} className="w-full bg-input border border-border rounded-xl px-3 py-2.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/30 resize-none" placeholder="Describe the learning objectives and content..." />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 block">AI Tutor Teaching Notes</label>
                <textarea rows={2} className="w-full bg-input border border-border rounded-xl px-3 py-2.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/30 resize-none" placeholder="How should the AI tutor present this content? Teaching style, key points to emphasize..." />
              </div>
            </div>
            <div className="border-2 border-dashed border-border rounded-xl p-5 text-center hover:border-primary/40 transition-colors cursor-pointer">
              <Upload size={24} className="mx-auto text-muted-foreground mb-2" />
              <p className="text-sm font-semibold text-foreground">Attach curriculum files</p>
              <p className="text-xs text-muted-foreground mt-0.5">PDF, DOCX, MP4, ZIP, SCORM</p>
            </div>
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl text-xs text-blue-700">
              <span className="font-semibold">Submission Pipeline:</span> Your content will go through Peer Review → Admin Approval → Publish to Catalog. Average review time: 3–5 business days.
            </div>
            <button className="w-full py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-semibold btn-scale hover:bg-primary/90 transition-colors">
              <Send size={14} className="inline mr-1.5" /> Submit for Review
            </button>
          </div>
        </div>
      )}

      {/* Educator Detail Modal */}
      <Modal open={educatorModalOpen} onClose={() => setEducatorModalOpen(false)} title="Educator Profile" size="sm">
        {selectedEducator && (
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                <span className="text-lg font-bold text-primary">{selectedEducator.avatar}</span>
              </div>
              <div>
                <h3 className="text-base font-bold text-foreground">{selectedEducator.name}</h3>
                <p className="text-sm text-muted-foreground">{selectedEducator.title}</p>
                <p className="text-xs text-muted-foreground">{selectedEducator.institution}</p>
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Credentials</p>
              <div className="flex flex-wrap gap-1.5">
                {selectedEducator.credentials.map((c) => (
                  <span key={c} className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-semibold">{c}</span>
                ))}
              </div>
            </div>
            {!selectedEducator.verified && (
              <div className="flex gap-2">
                <button className="flex-1 py-2.5 border border-border rounded-xl text-sm font-semibold text-muted-foreground hover:bg-muted transition-colors">Reject</button>
                <button className="flex-1 py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-semibold btn-scale hover:bg-primary/90 transition-colors">
                  <Shield size={14} className="inline mr-1.5" /> Verify Educator
                </button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
